import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  RefreshCw,
  RotateCcw,
  ChevronRight,
  ArrowLeft,
  ShoppingBag,
  CheckCircle2,
  CircleAlert,
  Loader2,
  FileText,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { clientApi, formatCOP } from "../lib/api";
import { formatUnit } from "../lib/units";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const STATUS_LABEL = {
  recibido: "Recibido",
  en_revision: "En revisión",
  confirmado: "Confirmado",
  enviado: "Enviado",
  cancelado: "Cancelado",
};

const STATUS_COLOR = {
  recibido: "bg-cm-yellow/30 text-amber-900 border-cm-yellow",
  en_revision: "bg-blue-100 text-blue-800 border-blue-300",
  confirmado: "bg-green-100 text-green-800 border-green-300",
  enviado: "bg-cm-ink text-cm-cream border-cm-ink",
  cancelado: "bg-red-100 text-red-800 border-red-300",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [repeatingId, setRepeatingId] = useState(null);
  const { add, clear } = useCart();
  const navigate = useNavigate();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.get("/portal/orders");
      setOrders(data || []);
    } catch (err) {
      toast.error("No pudimos cargar tu historial");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fast path: load orders WITHOUT reconciliation (instant on mobile).
        const { data } = await clientApi.get("/portal/orders");
        if (!cancelled) setOrders(data || []);
      } catch (err) {
        if (!cancelled) toast.error("No pudimos cargar tu historial");
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Then, in the background, fetch factura reconciliation and merge in.
      // If this call fails or times out, the orders still render fine.
      try {
        const { data: reconMap } = await clientApi.get("/portal/orders/reconciliation");
        if (cancelled || !reconMap) return;
        setOrders((prev) => prev.map((o) => {
          const key = String(o.arcos_order_number || "");
          const factura = key && reconMap[key] ? reconMap[key] : null;
          return factura ? { ...o, factura } : o;
        }));
      } catch { /* silent - reconciliation is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const repeatOrder = async (order) => {
    setRepeatingId(order.order_number);
    try {
      // Fetch current products to validate availability and refresh prices
      const { data: products } = await clientApi.get("/portal/products");
      const productMap = new Map(products.map((p) => [p.codigo, p]));

      const missing = [];
      const validLines = [];
      for (const line of order.lineas) {
        const current = productMap.get(line.codigo);
        if (!current || !current.precio || !current.disponible) {
          missing.push(line.descripcion);
          continue;
        }
        const stock = current.inventario;
        const qty =
          stock !== null && stock !== undefined && stock > 0
            ? Math.min(line.cantidad, stock)
            : line.cantidad;
        validLines.push({ product: current, qty });
      }

      if (validLines.length === 0) {
        toast.error("Ningún producto del pedido está disponible actualmente");
        return;
      }

      clear();
      validLines.forEach(({ product, qty }) => add(product, qty));

      if (missing.length > 0) {
        toast.warning(
          `${validLines.length} producto(s) añadido(s). ${missing.length} ya no están disponibles.`
        );
      } else {
        toast.success(`Tu pedido fue cargado al carrito (${validLines.length} productos)`);
      }
      navigate("/checkout");
    } catch (err) {
      toast.error("No pudimos repetir el pedido");
    } finally {
      setRepeatingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    return {
      count: orders.length,
      total,
      synced: orders.filter((o) => o.arcos_sync_status === "synced").length,
    };
  }, [orders]);

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="my-orders-page">
      <Header />
      <CartSheet />

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/catalogo"
          className="inline-flex items-center text-sm text-cm-ink-soft hover:text-cm-red mb-4"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Seguir comprando
        </Link>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
              Historial
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight flex items-center gap-3">
              <ClipboardList className="h-7 w-7 text-cm-red" />
              Mis pedidos
            </h1>
            <p className="text-sm text-cm-ink-soft mt-2 max-w-xl">
              Aquí encuentras todos los pedidos que has hecho a través del portal. Puedes repetir
              cualquier pedido con un clic.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchOrders}
            className="border-cm-border"
            data-testid="refresh-orders-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>

        {/* Educational banner: portal vs presencial + cartera link */}
        <div
          className="mb-6 rounded-lg border border-cm-border bg-cm-cream-alt/60 px-4 py-3 flex gap-3 items-start"
          data-testid="orders-info-banner"
        >
          <Info className="h-4 w-4 text-cm-ink-soft flex-shrink-0 mt-0.5" />
          <div className="text-xs text-cm-ink-soft leading-relaxed">
            Esta pantalla lista únicamente los <strong className="text-cm-ink">pedidos que hiciste por el portal B2B</strong>.
            Los pedidos que te toma un vendedor en ruta no aparecen aquí — solo llegan facturados a{" "}
            <Link to="/mi-cartera" className="text-cm-red font-medium hover:underline">Mi cartera</Link>.
            <br />
            <span className="text-cm-ink-soft/80">
              Si el valor final de la factura difiere del pedido, verás el badge naranja
              <span className="inline-flex items-center gap-1 mx-1 text-[10px] px-1.5 py-0.5 rounded-full border border-orange-300 bg-orange-50 text-orange-900 font-medium align-middle">
                <AlertTriangle className="h-2.5 w-2.5" /> Facturado con ajustes
              </span>
              — la factura es lo que finalmente te cobran.
            </span>
          </div>
        </div>

        {!loading && orders.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="orders-summary">
            <Card className="p-4 bg-white border-cm-border shadow-none">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">
                Pedidos
              </p>
              <p className="font-mono text-2xl font-bold mt-1">{stats.count}</p>
            </Card>
            <Card className="p-4 bg-white border-cm-border shadow-none">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">
                Acumulado
              </p>
              <p className="font-mono text-2xl font-bold mt-1 text-cm-red">
                {formatCOP(stats.total)}
              </p>
            </Card>
            <Card className="p-4 bg-white border-cm-border shadow-none">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">
                Sincronizados a ERP
              </p>
              <p className="font-mono text-2xl font-bold mt-1 text-green-700">
                {stats.synced}
              </p>
            </Card>
          </div>
        )}

        {loading ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
            <p className="text-sm text-cm-ink-soft mt-3">Cargando tu historial...</p>
          </Card>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center bg-white border-cm-border shadow-none">
            <ShoppingBag className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
            <h2 className="font-serif text-xl mt-4">Aún no tienes pedidos</h2>
            <p className="text-sm text-cm-ink-soft mt-2 mb-6">
              Cuando hagas tu primer pedido aparecerá aquí para que puedas consultarlo o repetirlo.
            </p>
            <Button asChild className="btn-cm-primary" data-testid="empty-go-catalog-btn">
              <Link to="/catalogo">Ir al catálogo</Link>
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => {
              const isOpen = expanded === o.order_number;
              const isRepeating = repeatingId === o.order_number;
              return (
                <Card
                  key={o.order_number}
                  className="bg-white border-cm-border shadow-none overflow-hidden"
                  data-testid={`order-card-${o.order_number}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : o.order_number)}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-cm-cream-alt/40 transition-colors"
                    data-testid={`order-toggle-${o.order_number}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-cm-red">
                          {o.order_number}
                        </span>
                        <Badge
                          className={`border ${STATUS_COLOR[o.estado] || ""} text-[10px] font-medium`}
                          variant="outline"
                        >
                          {STATUS_LABEL[o.estado] || o.estado}
                        </Badge>
                        {/* Invoice reconciliation badge: paid | match | adjusted | pending */}
                        {(o.factura?.status === "paid" || o.factura?.status === "match") && (
                          <Link
                            to="/mi-cartera"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-green-300 bg-green-50 text-green-800 hover:bg-green-100 transition-colors"
                            data-testid={`factura-badge-match-${o.order_number}`}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            {o.factura.status === "paid" ? "Facturado y pagado" : `Facturado · FAC-${o.factura.factura_id}`}
                          </Link>
                        )}
                        {(o.factura?.status === "adjusted" || o.factura?.status === "paid_adjusted") && (
                          <Link
                            to="/mi-cartera"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100 transition-colors font-medium"
                            data-testid={`factura-badge-adjusted-${o.order_number}`}
                            title={`Pediste ${formatCOP(o.total)}. Facturado como ${formatCOP(o.factura.factura_total)}. La factura es lo que cobran.`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {o.factura.status === "paid_adjusted"
                              ? `Pagado con ajustes · FAC-${o.factura.factura_id}`
                              : `Facturado con ajustes · FAC-${o.factura.factura_id}`}
                          </Link>
                        )}
                        {!o.factura && o.arcos_sync_status !== "synced" && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                            <CircleAlert className="h-3 w-3" /> Pendiente de sincronización
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-cm-ink-soft">
                        <span>{formatDate(o.created_at)}</span>
                        <span>·</span>
                        <span>
                          {o.lineas?.length || 0} producto
                          {o.lineas?.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg font-bold">{formatCOP(o.total)}</p>
                      <p className="text-[10px] text-cm-ink-soft uppercase tracking-widest">
                        Incl. IVA
                      </p>
                    </div>
                    <ChevronRight
                      className={`h-5 w-5 text-cm-ink-soft transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div
                      className="border-t border-cm-border bg-cm-cream-alt/30"
                      data-testid={`order-details-${o.order_number}`}
                    >
                      {/* Invoice reconciliation panel — visible only when there is a linked factura */}
                      {o.factura && (
                        <div
                          className={`px-5 py-3 border-b border-cm-border ${
                            (o.factura.status === "adjusted" || o.factura.status === "paid_adjusted") ? "bg-orange-50/60" : "bg-green-50/40"
                          }`}
                          data-testid={`factura-panel-${o.order_number}`}
                        >
                          <div className="flex items-start gap-3">
                            <FileText className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                              (o.factura.status === "adjusted" || o.factura.status === "paid_adjusted") ? "text-orange-700" : "text-green-700"
                            }`} />
                            <div className="flex-1 text-xs">
                              <p className="font-medium text-cm-ink">
                                {o.factura.status === "paid"
                                  ? "Este pedido ya se facturó y se pagó"
                                  : o.factura.status === "paid_adjusted"
                                    ? "Este pedido se facturó con ajustes y ya se pagó"
                                    : o.factura.status === "adjusted"
                                      ? "Este pedido se facturó con ajustes"
                                      : "Este pedido ya se facturó"}
                              </p>
                              <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1 mt-2">
                                <div>
                                  <span className="text-cm-ink-soft">Factura</span>
                                  <p className="font-mono font-semibold">FAC-{o.factura.factura_id}</p>
                                </div>
                                <div>
                                  <span className="text-cm-ink-soft">Total facturado</span>
                                  <p className={`font-mono font-semibold ${
                                    (o.factura.status === "adjusted" || o.factura.status === "paid_adjusted") ? "text-orange-800" : "text-cm-ink"
                                  }`}>
                                    {formatCOP(o.factura.factura_total)}
                                    {(o.factura.status === "adjusted" || o.factura.status === "paid_adjusted") && (
                                      <span className="text-[10px] font-normal ml-1 text-cm-ink-soft">
                                        (pedido: {formatCOP(o.total)})
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-cm-ink-soft">Saldo pendiente</span>
                                  <p className="font-mono font-semibold">
                                    {o.factura.status === "paid" || o.factura.status === "paid_adjusted"
                                      ? <span className="text-green-700">Pagada</span>
                                      : formatCOP(o.factura.factura_saldo)}
                                  </p>
                                </div>
                              </div>
                              <p className="mt-2 text-[11px] text-cm-ink-soft">
                                {o.factura.status === "paid" || o.factura.status === "paid_adjusted"
                                  ? "Esta factura ya no aparece en Mi cartera porque su saldo está en cero."
                                  : <>El valor y detalle final lo verás en{" "}
                                      <Link to="/mi-cartera" className="text-cm-red hover:underline font-medium">Mi cartera</Link>
                                      . La factura es lo que efectivamente se cobra.</>}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="px-5 py-4">
                        <div className="divide-y divide-cm-border bg-white rounded-md border border-cm-border">
                          {o.lineas.map((l) => (
                            <div
                              key={l.codigo}
                              className="flex items-start gap-3 px-4 py-3"
                              data-testid={`order-line-${o.order_number}-${l.codigo}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-mono text-cm-ink-soft">
                                  {l.codigo}
                                </p>
                                <p className="text-sm font-medium line-clamp-1">
                                  {l.descripcion}
                                </p>
                                <p className="text-[11px] text-cm-ink-soft">
                                  {l.categoria}
                                  {l.unidad ? ` · ${formatUnit(l.unidad)}` : ""}
                                </p>
                              </div>
                              <div className="text-right text-sm">
                                <p className="font-mono">
                                  {l.cantidad} × {formatCOP(l.precio_unitario)}
                                </p>
                                <p className="font-mono font-semibold">
                                  {formatCOP(l.subtotal)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {o.observaciones && (
                          <div className="mt-3 text-xs bg-white border border-cm-border rounded-md px-3 py-2">
                            <span className="text-cm-ink-soft">Observaciones: </span>
                            <span className="text-cm-ink">{o.observaciones}</span>
                          </div>
                        )}

                        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                          <div className="text-xs text-cm-ink-soft">
                            {o.lista_precios_nombre && (
                              <span>
                                Lista:{" "}
                                <span className="font-medium text-cm-ink">
                                  {o.lista_precios_nombre}
                                </span>
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => repeatOrder(o)}
                            disabled={isRepeating}
                            className="btn-cm-primary h-10"
                            data-testid={`repeat-order-btn-${o.order_number}`}
                          >
                            {isRepeating ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Cargando...
                              </>
                            ) : (
                              <>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Repetir pedido
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
